import { A } from '@adornis/base/env-info.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { switchMap } from 'rxjs';
import { Comment } from '../entities/comment.js';
import { ContactEntity } from '../entities/contact.js';
import { IntercomEntity } from '../entities/intercom.js';
import { CommentHelperEntity, OdooEntity } from '../entities/odoo.js';

export const subscribeOdooEntityCommentsPaginated = registerSubscription({
  operationName: 'subscribeOdooEntityCommentsPaginated',
  type: () => [CommentHelperEntity],
  resolve: (skip: number, limit: number) => (gqlFields: BaseQLSelectionSet<CommentHelperEntity>) => {
    return watchEntireCollection(
      OdooEntity._collectionName,
      // { comments: { $exists: true, $ne: null, $not: { $size: 0 } } },
      // { projection: { _id: 1 } },
    ).pipe(
      switchMap(async () => {
        const col = await getRawCollection(OdooEntity._collectionName);
        const result = await col
          .aggregate([
            {
              $project: { _id: 1, comments: 1, odooTicketID: 1, createdAt: 1 },
            },
            { $unwind: '$comments' },
            { $sort: { 'comments.createdAt': -1 } },
            { $skip: skip },
            { $limit: limit },
          ])
          .toArray();
        return result.map(
          item =>
            new CommentHelperEntity({
              _id: A.getGloballyUniqueID(),
              sourceID: item._id,
              comment: item.comments,
              odooTicketID: item.odooTicketID,
              timestamp: item.createdAt,
            }),
        );
      }),
    );
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const subscribeIntercomEntityCommentsPaginated = registerSubscription({
  operationName: 'subscribeIntercomEntityCommentsPaginated',
  type: () => [CommentHelperEntity],
  resolve: (skip: number, limit: number) => (gqlFields: BaseQLSelectionSet<CommentHelperEntity>) => {
    return watchQuery(
      IntercomEntity._collectionName,
      { comments: { $exists: true, $ne: null, $not: { $size: 0 } } },
      { projection: { _id: 1 } },
    ).pipe(
      switchMap(async () => {
        const col = await getRawCollection(IntercomEntity._collectionName);
        const result = await col
          .aggregate([
            {
              $project: { _id: 1, comments: 1, intercomId: 1, createdAt: 1 },
            },
            { $unwind: '$comments' },
            { $sort: { createdAt: -1 } },
            { $skip: skip },
            { $limit: limit },
          ])
          .toArray();
        return result.map(
          item =>
            new CommentHelperEntity({
              _id: A.getGloballyUniqueID(),
              sourceID: item.intercomId || item._id,
              comment: item.comments,
              timestamp: item.createdAt,
            }),
        );
      }),
    );
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const subscribeContactEntityCommentsPaginated = registerSubscription({
  operationName: 'subscribeContactEntityCommentsPaginated',
  type: () => [CommentHelperEntity],
  resolve: (skip: number, limit: number) => (gqlFields: BaseQLSelectionSet<CommentHelperEntity>) => {
    return watchQuery(
      ContactEntity._collectionName,
      { notes: { $exists: true, $ne: null, $not: { $size: 0 } } },
      { projection: { _id: 1 } },
    ).pipe(
      switchMap(async () => {
        const col = await getRawCollection(ContactEntity._collectionName);
        const result = await col
          .aggregate([
            {
              $project: { _id: 1, notes: 1, odooTicketID: 1, timestamp: 1 },
            },
            { $unwind: '$notes' },
            { $sort: { timestamp: -1 } },
            { $skip: skip },
            { $limit: limit },
          ])
          .toArray();
        return result.map(
          item =>
            new CommentHelperEntity({
              _id: A.getGloballyUniqueID(),
              sourceID: item._id,
              comment: item.notes,
              odooTicketID: item.odooTicketID,
              timestamp: item.timestamp,
            }),
        );
      }),
    );
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const addCommentToOdooEntity = registerMutation({
  operationName: 'addCommentToOdooEntity',
  type: () => Comment,
  resolve: (comment: string, odooEntityID: string) => async (gqlFields: BaseQLSelectionSet<Comment>) => {
    const commentEntity = new Comment({
      _id: A.getGloballyUniqueID(),
      text: comment,
      authorID: context.userID ?? 'server',
      createdAt: new Date(),
    });

    const col = await getCollection(OdooEntity);
    await col.updateOne({ _id: odooEntityID }, [
      {
        $set: {
          comments: {
            $concatArrays: [{ $ifNull: ['$comments', []] }, [commentEntity.toJSON()]],
          },
        },
      },
    ]);

    return commentEntity;
  },
  params: [
    { name: 'comment', type: () => String },
    { name: 'odooEntityID', type: () => String },
  ],
});

export const addCommentToContactEntity = registerMutation({
  operationName: 'addCommentToContactEntity',
  type: () => Comment,
  resolve: (comment: string, contactEntityID: string) => async (gqlFields: BaseQLSelectionSet<Comment>) => {
    const commentEntity = new Comment({
      _id: A.getGloballyUniqueID(),
      text: comment,
      authorID: context.userID ?? 'server',
      createdAt: new Date(),
    });

    const col = await getCollection(ContactEntity);
    await col.updateOne({ _id: contactEntityID }, [
      {
        $set: {
          notes: {
            $concatArrays: [{ $ifNull: ['$notes', []] }, [commentEntity.toJSON()]],
          },
        },
      },
    ]);

    return commentEntity;
  },
  params: [
    { name: 'comment', type: () => String },
    { name: 'contactEntityID', type: () => String },
  ],
});

export const addCommentToIntercomEntity = registerMutation({
  operationName: 'addCommentToIntercomEntity',
  type: () => Comment,
  resolve: (comment: string, intercomEntityID: string) => async (gqlFields: BaseQLSelectionSet<Comment>) => {
    const commentEntity = new Comment({
      _id: A.getGloballyUniqueID(),
      text: comment,
      authorID: context.userID ?? 'server',
      createdAt: new Date(),
    });

    const col = await getCollection(IntercomEntity);
    await col.updateOne({ _id: intercomEntityID }, [
      {
        $set: {
          comments: {
            $concatArrays: [{ $ifNull: ['$comments', []] }, [commentEntity.toJSON()]],
          },
        },
      },
    ]);

    return commentEntity;
  },
  params: [
    { name: 'comment', type: () => String },
    { name: 'intercomEntityID', type: () => String },
  ],
});
